import { useCallback, useRef } from 'react';

type TAudioWindow = typeof window & {
    webkitAudioContext?: typeof AudioContext;
};

const NOTE_FREQUENCIES_HZ = [659.25, 880];
const NOTE_GAP_S = 0.12;
const SILENT_GAIN = 0.0001;
const PEAK_GAIN = 0.11;
const ATTACK_S = 0.025;
const RELEASE_S = 0.22;
const NOTE_DURATION_S = 0.23;
const RUNNING_STATE = 'running';
const SUSPENDED_STATE = 'suspended';

/**
 * Two-note chime played when an assistant response arrives.
 * `prepare` must run inside a user gesture so the browser allows audio.
 */
export const useResponseSound = () => {
    const audioContextRef = useRef<AudioContext | null>(null);

    const prepare = useCallback(() => {
        try {
            const audioWindow = window as TAudioWindow;
            const AudioContextClass =
                audioWindow.AudioContext || audioWindow.webkitAudioContext;
            if (!AudioContextClass) return;
            if (!audioContextRef.current)
                audioContextRef.current = new AudioContextClass();
            if (audioContextRef.current.state === SUSPENDED_STATE)
                audioContextRef.current.resume();
        } catch (_) {
            audioContextRef.current = null;
        }
    }, []);

    const play = useCallback(() => {
        const audioContext = audioContextRef.current;
        if (!audioContext || audioContext.state !== RUNNING_STATE) return;
        const startAt = audioContext.currentTime;
        NOTE_FREQUENCIES_HZ.forEach((frequency, index) => {
            const oscillator = audioContext.createOscillator();
            const gain = audioContext.createGain();
            const noteStart = startAt + index * NOTE_GAP_S;
            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(frequency, noteStart);
            gain.gain.setValueAtTime(SILENT_GAIN, noteStart);
            gain.gain.exponentialRampToValueAtTime(
                PEAK_GAIN,
                noteStart + ATTACK_S
            );
            gain.gain.exponentialRampToValueAtTime(
                SILENT_GAIN,
                noteStart + RELEASE_S
            );
            oscillator.connect(gain);
            gain.connect(audioContext.destination);
            oscillator.start(noteStart);
            oscillator.stop(noteStart + NOTE_DURATION_S);
        });
    }, []);

    return { prepare, play };
};
